import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import React, { use, useEffect, useState } from "react";
import parse from "html-react-parser";

import "swiper/css";
import CheapestCarCard from "./CheapestCarCard";
import { observer } from "mobx-react-lite";
import { findLocale } from "@/locales";
import Placeholder from "./Placeholder";
import MobilePlaceholder from "./MobilePlaceholder";
import { getUniqueAgencies, getUniqueCarClasses } from "@/utils/uniqueParse";

const DriveAround = ({
  type,
  results,
  locale,
  name,
  locationName,
  airportData,
}: {
  type: string;
  results: Result;
  locale?: string;
  name: string;
  locationName: string;
  airportData: AirportNameData;
}) => {
  const handleViewAll = () => {
    document.getElementById("search_button")?.click();
  };

  const [isUserAgentBot, setIsUserAgentBot] = useState(true);
  useEffect(() => {
    if (!/bot/i.test(navigator.userAgent)) setIsUserAgentBot(false);
  }, []);
  // Function that returns the policy string based on the policy value
  const returnPolicyStrings = (policyValue: string) => {
    if (policyValue === "CANCEL") {
      return "Free cancellation  allows you to cancel your booking at a time of your choice without losing any money.";
    } else if (policyValue === "FUEL") {
      return "Fair Fuel Policy means that you receive a full tank of fuel, which allows you to cover most distance.";
    } else if (policyValue === "MILEAGE") {
      return "Unlimited mileage allows you to drive as many miles as you want without paying extra. This offers a great advantage for you, especially if you are traveling to a spot that is far away from the airport.";
    }
  };

  const now = new Date();
  const updatedAt = results?.updatedAt ? new Date(results.updatedAt) : now;
  const timeDiff = Math.floor((now.getTime() - updatedAt.getTime()) / 1000);
  const minutes = Math.floor(timeDiff / 60);
  const hours = Math.floor(minutes / 60);

  const updatedAtString =
    timeDiff === 0
      ? " Just Now"
      : timeDiff > 86400
      ? " 23 hours ago"
      : ` ${hours > 0 ? `${hours} Hours and` : ""} ${
          minutes - hours * 60
        } Minute${minutes === 1 ? "" : "s"} ago`;

  // Function that generates a random string based on the price
  const generatePriceString = (price: number) => {
    const pickRandomToGenerate = (array: string[]) => {
      const randomIndex = Math.floor(Math.random() * array.length);
      return array[randomIndex];
    };
    const startString = `you know, `;
    let strings: string[] = [];
    if (price <= 80) {
      if (price <= 10) {
        strings = [
          `a reserve brew from Starbucks, the item that the coffee retailer giant calls "Our rarest coffees, small-batch roasted in Seattle."`,
          `an Ultimate steak footlong from Subway.`,
        ];
      } else if (price <= 20) {
        strings = [
          `an Egg cooker if you are a cooking enthusiast.`,
          `tin cups.`,
        ];
      } else if (price <= 30) {
        strings = [
          `a week of Starbucks coffee. It's that cheap!`,
          `Movie tickets for two. Why go to a movie when you can have fun exploring the world?`,
          `two new t-shirts from Target.`,
        ];
      } else if (price <= 40) {
        strings = [
          `a nice sushi dinner for one.`,
          `A Bean Bag Chair from Walmart.`,
        ];
      } else if (price <= 50) {
        strings = [`Woolen blankets.`];
      } else if (price <= 60) {
        strings = [`A houseplant.`, `a video game.`];
      } else if (price <= 70) {
        strings = [
          `ten months of Netflix subscription.`,
          `Lunch for four at a nice restaurant.`,
        ];
      } else if (price <= 80) {
        strings = [`the Keurig K-Mini Coffee Maker price.`];
      }
      return (
        startString + `less than the price of ` + pickRandomToGenerate(strings)
      );
    } else {
      if (price <= 90) {
        strings = [`and create memories that last forever.`];
      } else {
        strings = [`helping you enjoy a memorable, comfy trip.`];
      }
      return pickRandomToGenerate(strings);
    }
  };

  const generateCheapestContent = (locationName: string, data?: any) => {
    if (!data)
      return `One of the biggest advantages of booking a car rental at ${locationName} is the cheapest rates ${airportData.cityOnly} rental car companies can offer you. All the rental companies like Enterprise, Sixt and Hertz are more than willing to offer you the most Affordable car rental rate in ${locationName}. Average prices start from $50/day or $300/week.\nWhether you are looking for a long-term car rental in ${locationName} or a Luxury car rental in ${locationName}, you will find a deal that works for you.\nRemember the special weekend rates; it might cost you up to $60/day if you book on the weekends.\nWhether you are looking for a Luxury, Economy, Sedan, etc., you can find one for an affordable rate at car rental locations in ${locationName}.\nThese cars usually have features like Fair Fuel Pricing, Unlimited Mileage and Free Cancellation making them irresistible.\nIf you want to know more about the cars you can rent at ${locationName}, please browse through the collection below.`;

    const providers = getUniqueAgencies(data?.car_data);
    const classes = getUniqueCarClasses(data?.cheapest_cars);
    const providerText =
      providers.length === 1
        ? providers[0].agency.name
        : providers
            .map((p) => p.agency.name)
            .join(", ")
            .replace(/,(?=[^,]*$)/, " and");
    const policyText = data.cheapest_cars[0].filter.policies
      ? data.cheapest_cars[0].filter.policies === 1
        ? populatePolicies(data.cheapest_cars[0].filter.policies)[0]
            .valueDisplay
        : populatePolicies(data.cheapest_cars[0].filter.policies)
            .map((policy: any) => policy.valueDisplay)
            .join(", ")
            .replace(/,(?=[^,]*$)/, " and")
      : ``;
    const formattedText = `${
      providers.length === 1
        ? `One of the biggest advantages of booking a  car rental at ${locationName} is the cheapest rate ${providerText} can offer you. ${providerText} has some great deals waiting for you starting at <strong className="font-bold">${
            data.cheapest_cars[0].displayTotalPrice
          }/day</strong> or <strong className="font-bold">${
            data.cheapest_cars[0].displayFullPrice[0]
          }${Math.ceil(results.cheapest_cars[0].totalPrice) * 6}/week</strong>`
        : `<p>One of the biggest advantages of booking a car rental at ${locationName} is the cheapest rates ${
            airportData.cityOnly
          } rental car companies can offer you. All the rental companies like ${providerText} are more than willing to offer you the most Affordable car rental rate in ${locationName}. Average prices start from <strong className="font-bold">${
            data.cheapest_cars[0].displayTotalPrice
          }/day</strong> or <strong className="font-bold">${
            data.cheapest_cars[0].displayFullPrice[0]
          }${Math.ceil(results.cheapest_cars[0].totalPrice) * 6}/week</strong>`
    }.</p><p>Whether you are looking for a long-term car rental in ${locationName} or a Luxury car rental in ${locationName}, you will find a deal that works for you here.</p><p>Please keep the special weekend rates in mind while booking. It might cost you up to ${
      results.cheapest_cars.length > 1 ?results.cheapest_cars.slice(0,-1)[0].displayTotalPrice:results.cheapest_cars[0].displayTotalPrice
    }/day if you book on the weekends. </p><p>Whether you are looking for a ${
      classes.length === 1
        ? classes[0]
        : classes
            .map(
              (carClass) => `<strong className="font-bold">${carClass}</strong>`
            )
            .join(", ")
    }, etc., you can find one for an affordable rate at car rental locations in ${locationName}.<p/><p>These cars usually have features like ${policyText}, making them irresistible.<p/><p>If you want to know more about the cars you can rent at ${locationName}, please browse through the collection below.</p>`;
    return formattedText;
  };
  const generateTopContent = (locationName: string, data?: any) => {
    if (!data)
      return `<p>The best car deal you can get in ${
        locationName.split(",")[0]
      } is the Class Special Car (Economy) or similar. This is ideal for you especially if you are a group of 4 or lower, as it can comfortably accommodate up to 4.<p/><p>You can now rent Class Special Car (Economy) for just $50/day, you know, less than Woolen blankets.<p/><p>There are other great rental car deals available in ${locationName}.<p/><p>Below is the list of popular Affordable car rentals in ${locationName} based on our user data.
    </p>`;

    const availablePolicies = populatePolicies(data?.filter?.policies);

    return `<p>The best rental car deal you can get in ${
      locationName.split(",")[0]
    } is ${data?.car?.brand ?? `Class Special Car`} (${
      data?.car?.carclass ?? `Economy`
    }) or similar. This is ideal for you, especially if you are a group of ${
      data?.car?.passengers ?? `4`
    } or lower, as it can comfortably accommodate up to ${
      data?.car?.passengers ?? `4`
    }.</p><p>You can now rent ${data?.car?.brand ?? `Class Special Car`} (${
      data?.car?.carclass ?? `Economy`
    }) for just ${
      data?.displayTotalPrice ? `${data?.displayTotalPrice}` : "$50"
    }/ day, ${
      data.totalPrice
        ? generatePriceString(data.totalPrice)
        : generatePriceString(50)
    }</p><p> ${
      data?.filter?.policies.length === 0
        ? ""
        : `${data?.car?.brand ?? `Class Special Car`} (${
            data?.car?.carclass ?? `Economy`
          }) comes with features such as ${
            data.filter.policies.length === 1
              ? populatePolicies(data.filter.policies)[0].valueDisplay
              : populatePolicies(data.filter.policies)
                  .map(
                    (policy: any) =>
                      `<strong className="font-bold">${policy.valueDisplay}</strong>`
                  )
                  .join(", ")
                  .replace(/,(?=[^,]*$)/, " and") || ""
          } which makes it the best of the bunch.</p>`
    }
    ${
      availablePolicies.length !== 0
        ? availablePolicies
            .map((policy: any) => `<p>${returnPolicyStrings(policy.value)}</p>`)
            .join("")
        : ""
    }
<p>There are other great rental car deals available in ${locationName}. Below is the list of popular <strong className="font-bold">Affordable car rentals</strong> in ${locationName} based on our user data.</p>`;
  };

  const policies = [
    {
      value: "CANCEL",
      valueDisplay: "Free cancellation",
    },
    {
      value: "FUEL",
      valueDisplay: "Fair fuel policy",
    },
    {
      value: "MILEAGE",
      valueDisplay: "Unlimited mileage",
    },
  ];

  const invalidClass = [
    "SPECIAL",
    "SPECIAL_SUV",
    "SPECIAL_WAGON",
    "SPECIAL_SEDAN",
    "SPECIAL_COUPE",
    "SPECIAL_VAN",
  ];

  const populatePolicies = (policyArray: number[]) => {
    const policyArr: any = [];
    policyArray?.map((policy: number) => {
      if (policy >= 3) return;
      policyArr.push(policies[policy]);
    });
    return policyArr;
  };

  // const userAgent = navigator.userAgent;
  // console.log(userAgent);

  return (
    <>
      <div className="mt-5 lg:mt-[60px] bg-white py-[30px]">
        <div className="mx-auto max-w-7xl">
          <div className="w-full px-[16px]">
            <div className=" mb-[18px] flex justify-between items-center">
              <h2 className="text-base font-extrabold lg:text-3xl text-my-dark-blue">
                {name === "Top"
                  ? `Best deals for cars to rent at ${locationName}
                  `
                  : name === "Cheapest"
                  ? `Cheapest cars to rent in ${locationName.replace(
                      /^(.+),\s(.+)$/,
                      "$2 at $1"
                    )} (Starting from ${
                      results?.lowestPrice
                        ? `${findLocale(locale!)?.currencySymbol}${
                            results.lowestPrice
                          }`
                        : "$50"
                    }  onwards)
                  `
                  : `Most booked cars to rent at ${locationName}`}
              </h2>
              <p
                onClick={handleViewAll}
                className="cursor-pointer hover:underline hidden md:inline text-[#004DE3] lg:text-base text-sm whitespace-nowrap"
              >
                View All
              </p>
            </div>
            <div className="pb-8 space-y-6 text-sm font-normal lg:pb-0 text-my-light-blue lg:font-light lg:text-base">
              {name === "Top"
                ? parse(
                    generateTopContent(
                      locationName,
                      results?.recommended_cars[0]
                    )
                  )
                : name === "Cheapest"
                ? parse(generateCheapestContent(locationName, results))
                : `Here are the most booked rental cars by our users. If you still need help figuring out what car suits you, you can check them out. Browse through them and find the rental car at ${locationName} that will fit your needs.`}
            </div>
          </div>

          <div>
            {isUserAgentBot && (
              <table className="w-full my-8">
                <caption>{`${name} cars to rent at ${locationName}`}</caption>
                <tr>
                  <th>Car Name</th>
                  <th>Car Class</th>
                  <th>Car Features</th>
                  <th>Car Agency</th>
                  <th>Car Policies</th>
                  <th>Car Price</th>
                  {/* <th>Book</th> */}
                </tr>
                {name === "Cheapest" &&
                  results?.cheapest_cars
                    ?.filter((a) => !invalidClass.includes(a.car.category))
                    .slice(0, 4)
                    .map((data: CarData) => {
                      const features = [
                        data.car.luggage && `Luggage ${data.car.luggage}`,
                        data.car.passengers &&
                          `Passengers ${data.car.passengers}`,
                        data.car.airconditioning && `Air Conditioning`,
                        data.car.doors && `Doors ${data.car.doors}`,
                      ].filter((a) => a !== undefined);
                      return (
                        <tr key={data.id}>
                          <td>{data.car.brand}</td>
                          <td>{data.car.carclass}</td>
                          <td>{features.join(", ")}</td>
                          <td>{data.agency.name}</td>
                          <td>
                            {populatePolicies(data.filter.policies)
                              .map((a: any) => a.valueDisplay)
                              .join(",")}
                          </td>
                          <td>
                            {data.displayTotalPrice || data.displayFullPrice}
                          </td>
                          {/* <td>{data.shareURL}</td> */}
                        </tr>
                      );
                    })}
                {name === "Top" &&
                  results?.recommended_cars
                    ?.filter((a) => !invalidClass.includes(a.car.category))
                    .slice(0, 4)
                    .map((data: CarData) => {
                      const features = [
                        data.car.luggage && `Luggage ${data.car.luggage}`,
                        data.car.passengers &&
                          `Passengers ${data.car.passengers}`,
                        data.car.airconditioning && `Air Conditioning`,
                        data.car.doors && `Doors ${data.car.doors}`,
                      ].filter((a) => a !== undefined);
                      return (
                        <tr key={data.id}>
                          <td>{data.car.brand}</td>
                          <td>{data.car.carclass}</td>
                          <td>{features.join(", ")}</td>
                          <td>{data.agency.name}</td>
                          <td>
                            {populatePolicies(data.filter.policies)
                              .map((a: any) => a.valueDisplay)
                              .join(",")}
                          </td>
                          <td>
                            {data.displayTotalPrice || data.displayFullPrice}
                          </td>
                          {/* <td>{data.shareURL}</td> */}
                        </tr>
                      );
                    })}
                {name === "Most Booked" &&
                  results?.most_popular_cars
                    ?.filter((a) => !invalidClass.includes(a.car.category))
                    .slice(0, 4)
                    .map((data: CarData) => {
                      const features = [
                        data.car.luggage && `Luggage ${data.car.luggage}`,
                        data.car.passengers &&
                          `Passengers ${data.car.passengers}`,
                        data.car.airconditioning && `Air Conditioning`,
                        data.car.doors && `Doors ${data.car.doors}`,
                      ].filter((a) => a !== undefined);
                      return (
                        <tr key={data.id}>
                          <td>{data.car.brand}</td>
                          <td>{data.car.carclass}</td>
                          <td>{features.join(", ")}</td>
                          <td>{data.agency.name}</td>
                          <td>
                            {populatePolicies(data.filter.policies)
                              .map((a: any) => a.valueDisplay)
                              .join(",")}
                          </td>
                          <td>
                            {data.displayTotalPrice || data.displayFullPrice}
                          </td>
                          {/* <td>{data.shareURL}</td> */}
                        </tr>
                      );
                    })}
              </table>
            )}
            {!isUserAgentBot && (
              <>
                <div
                  className={`mt-[18px] px-[16px] lg:mt-[40px] lg:grid ${
                    type === "home"
                      ? "md:grid-cols-2 lg:grid-cols-3"
                      : "md:grid-cols-2"
                  } gap-3 hidden`}
                >
                  {results === null || !results ? <Placeholder /> : <></>}

                  {name === "Cheapest" &&
                    !isUserAgentBot &&
                    results?.cheapest_cars
                      ?.filter((a) => !invalidClass.includes(a.car.category))
                      .slice(0, 4)
                      .map((data: any) => {
                        let total = 0;
                        data.providers.forEach((provider: any) => {
                          let price = provider.totalPrice;
                          total += price;
                        });
                        return (
                          <CheapestCarCard
                            fuel={data.fuel}
                            prepaid={data.isPrepaid}
                            key={data.id}
                            category={data.car.category}
                            carImg={data.car.thumb}
                            luggage={data.car.luggage}
                            size={data?.car?.carclass}
                            name={data.car.brand}
                            passengers={data.car.passengers}
                            doors={data.car.doors}
                            cost={
                              data.displayTotalPrice || data.displayFullPrice
                            }
                            airconditioning={data.car.airconditioning}
                            data={data}
                            providerLogo={data.providers[0].logoUrl}
                            totalPrice={Math.ceil(total)}
                            sites={data.providers.length}
                            bookUrl={
                              // results===null || !results ?
                              data.shareURL
                              //  : data.providers[0].url
                            }
                            policies={populatePolicies(data.filter.policies)}
                          />
                        );
                      })}
                  {name === "Top" &&
                    !isUserAgentBot &&
                    results?.recommended_cars
                      ?.filter((a) => !invalidClass.includes(a.car.category))
                      .slice(0, 4)
                      .map((data: any) => {
                        let total = 0;
                        data.providers.forEach((provider: any) => {
                          let price = provider.totalPrice;
                          total += price;
                        });
                        return (
                          <CheapestCarCard
                            fuel={data.fuel}
                            prepaid={data.isPrepaid}
                            key={data.id}
                            category={data.car.category}
                            carImg={data.car.thumb}
                            luggage={data.car.luggage}
                            size={data?.car?.carclass}
                            name={data.car.brand}
                            passengers={data.car.passengers}
                            doors={data.car.doors}
                            cost={
                              data.displayTotalPrice || data.displayFullPrice
                            }
                            airconditioning={data.car.airconditioning}
                            data={data}
                            providerLogo={data.providers[0].logoUrl}
                            totalPrice={Math.ceil(total)}
                            sites={data.providers.length}
                            bookUrl={data.shareURL}
                            policies={populatePolicies(data.filter.policies)}
                          />
                        );
                      })}
                  {name === "Most Booked" &&
                    !isUserAgentBot &&
                    results?.most_popular_cars
                      ?.filter((a) => !invalidClass.includes(a.car.category))
                      .slice(0, 4)
                      .map((data: any) => {
                        let total = 0;
                        data.providers.forEach((provider: any) => {
                          let price = provider.totalPrice;
                          total += price;
                        });
                        return (
                          <CheapestCarCard
                            fuel={data.fuel}
                            prepaid={data.isPrepaid}
                            key={data.id}
                            category={data.car.category}
                            carImg={data.car.thumb}
                            luggage={data.car.luggage}
                            size={data?.car?.carclass}
                            name={data.car.brand}
                            passengers={data.car.passengers}
                            doors={data.car.doors}
                            cost={
                              data.displayTotalPrice || data.displayFullPrice
                            }
                            airconditioning={data.car.airconditioning}
                            data={data}
                            providerLogo={data.providers[0].logoUrl}
                            totalPrice={Math.ceil(total)}
                            sites={data.providers.length}
                            bookUrl={data.shareURL}
                            policies={populatePolicies(data.filter.policies)}
                          />
                        );
                      })}
                </div>
                <div className="grid lg:hidden">
                  {results === null || !results ? <MobilePlaceholder /> : <></>}
                </div>
                <div className="grid lg:hidden">
                  <Swiper
                    slidesPerView={1.1}
                    spaceBetween={16}
                    modules={[Autoplay, Pagination]}
                    autoplay
                    pagination={{ clickable: true }}
                    className="w-full h-full mySwiper"
                  >
                    {name === "Cheapest" &&
                      results?.cheapest_cars
                        ?.filter((a) => !invalidClass.includes(a.car.category))
                        .slice(0, 4)
                        .map((data: any) => {
                          let total = 0;
                          data.providers.forEach((provider: any) => {
                            let price = provider.totalPrice;

                            total += price;
                          });

                          return (
                            <SwiperSlide
                              key={data.id}
                              className="w-full rounded-2xl"
                            >
                              <CheapestCarCard
                                fuel={data.fuel}
                                prepaid={data.isPrepaid}
                                key={data.id}
                                category={data.car.category}
                                carImg={data.car.thumb}
                                luggage={data.car.luggage}
                                size={data?.car?.carclass}
                                name={data.car.brand}
                                passengers={data.car.passengers}
                                doors={data.car.doors}
                                cost={
                                  data.displayTotalPrice ||
                                  data.displayFullPrice
                                }
                                airconditioning={data.car.airconditioning}
                                data={data}
                                providerLogo={data.providers[0].logoUrl}
                                totalPrice={Math.ceil(total)}
                                sites={data.providers.length}
                                bookUrl={data.shareURL}
                                policies={populatePolicies(
                                  data.filter.policies
                                )}
                              />
                            </SwiperSlide>
                          );
                        })}
                    {name === "Top" &&
                      results?.recommended_cars
                        ?.filter((a) => !invalidClass.includes(a.car.category))
                        .slice(0, 4)
                        .map((data: any) => {
                          let total = 0;
                          data.providers.forEach((provider: any) => {
                            let price = provider.totalPrice;
                            total += price;
                          });
                          return (
                            <SwiperSlide
                              key={data.id}
                              className="w-full rounded-2xl"
                            >
                              <CheapestCarCard
                                fuel={data.fuel}
                                prepaid={data.isPrepaid}
                                key={data.id}
                                category={data.car.category}
                                carImg={data.car.thumb}
                                luggage={data.car.luggage}
                                size={data?.car?.carclass}
                                name={data.car.brand}
                                passengers={data.car.passengers}
                                doors={data.car.doors}
                                cost={
                                  data.displayTotalPrice ||
                                  data.displayFullPrice
                                }
                                airconditioning={data.car.airconditioning}
                                data={data}
                                providerLogo={data.providers[0].logoUrl}
                                totalPrice={Math.ceil(total)}
                                sites={data.providers.length}
                                bookUrl={data.shareURL}
                                policies={populatePolicies(
                                  data.filter.policies
                                )}
                              />
                            </SwiperSlide>
                          );
                        })}
                    {name === "Most Booked" &&
                      results?.most_popular_cars
                        ?.filter((a) => !invalidClass.includes(a.car.category))
                        .slice(0, 4)
                        .map((data: any) => {
                          let total = 0;
                          data.providers.forEach((provider: any) => {
                            let price = provider.totalPrice;
                            total += price;
                          });
                          return (
                            <SwiperSlide
                              key={data.id}
                              className="w-full rounded-2xl"
                            >
                              <CheapestCarCard
                                fuel={data.fuel}
                                prepaid={data.isPrepaid}
                                key={data.id}
                                category={data.car.category}
                                carImg={data.car.thumb}
                                luggage={data.car.luggage}
                                size={data?.car?.carclass}
                                name={data.car.brand}
                                passengers={data.car.passengers}
                                doors={data.car.doors}
                                cost={
                                  data.displayTotalPrice ||
                                  data.displayFullPrice
                                }
                                airconditioning={data.car.airconditioning}
                                data={data}
                                providerLogo={data.providers[0].logoUrl}
                                totalPrice={Math.ceil(total)}
                                sites={data.providers.length}
                                bookUrl={data.shareURL}
                                policies={populatePolicies(
                                  data.filter.policies
                                )}
                              />
                            </SwiperSlide>
                          );
                        })}
                  </Swiper>
                </div>
              </>
            )}
          </div>
          <p className="p-4 pb-0 text-sm italic text-[#595959]">
            Last Updated:{updatedAtString}
          </p>
        </div>
      </div>
    </>
  );
};

export default observer(DriveAround);
